import React from 'react';
import { createRoot } from 'react-dom/client';
import { SiteWideNotice, CookieConsentNotice, Navbar } from './reusablePartials';
import { AppProvider } from './context/AppState.jsx';
import { ModalProvider } from './context/modalContext.jsx';
import useAppState from './hooks/useAppState.jsx';
import useInitializeAppData from './hooks/useInitializeAppData.js';
import i18n from './i18n';

// Function to clean up legacy navbar event handlers
function cleanupLegacyNavbar() {
  // Remove event listeners from legacy navbar elements
  const legacyElements = ['#country_info_menu_link', '#subject_info_menu_link', '#about_info_menu_link', '#country_info_menu', '#subject_info_menu', '#about_info_menu', '#info-menu-content-search-input', '#info-menu-content-search-icon'];

  legacyElements.forEach((selector) => {
    const element = document.querySelector(selector);
    if (element) {
      // Clone node to remove all event listeners
      const newElement = element.cloneNode(true);
      element.parentNode.replaceChild(newElement, element);
    }
  });
}

// Function to mount React navbar on non-React pages
function mountReactNavbar() {
  // Check if we're on a React page (data.html, embed.html, locations/*, measures/*)
  const pathname = window.location.pathname;
  const isReactPage = pathname === '/data.html' || pathname === '/embed.html' || /\/locations\//.test(pathname) || /\/measures\//.test(pathname);

  // Only mount on non-React pages
  if (!isReactPage) {
    // Check if React navbar already exists
    if (document.getElementById('react-navbar-root')) {
      return;
    }

    // Clean up legacy navbar handlers
    cleanupLegacyNavbar();

    // Create a container for the React navbar
    const navbarContainer = document.createElement('div');
    navbarContainer.id = 'react-navbar-root';

    // Find the header element where the static navbar is
    const header = document.querySelector('header');
    if (header) {
      // Store breadcrumb if it exists
      const breadcrumb = header.querySelector('.breadcrumb-wrapper');

      // Replace the entire header content with React navbar
      header.innerHTML = '';
      header.appendChild(navbarContainer);

      // Re-add breadcrumb if it existed
      if (breadcrumb) {
        header.appendChild(breadcrumb);
      }
    } else {
      // Fallback: insert at the beginning of body
      document.body.insertBefore(navbarContainer, document.body.firstChild);
    }

    // Create the navbar app component with context
    const NavbarApp = () => {
      const [state, dispatch] = useAppState();

      // Load essential data for navbar
      useInitializeAppData(state, dispatch, {
        errorContext: 'navbar',
      });

      return (
        <AppProvider value={[state, dispatch]}>
          <ModalProvider>
            <SiteWideNotice />
            <CookieConsentNotice />
            <div className="uk-container">
              <Navbar />
            </div>
          </ModalProvider>
        </AppProvider>
      );
    };

    // Mount the React navbar using createRoot
    const root = createRoot(navbarContainer);
    root.render(<NavbarApp />);
  }
}

// Mount navbar when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountReactNavbar);
} else {
  // Use setTimeout to ensure other scripts have loaded
  setTimeout(mountReactNavbar, 0);
}
